/* eslint-disable no-unused-vars */
import { batch } from "solid-js";
import FeatureStore from "@rock-solid/feature-store";

import User, { UserUsa } from "@repo/kikoff-models/User";
import usRpc from "@repo/protobuf/api/usRpc";
import { Dismissable as UsDismissablePb } from "@repo/protobuf/gen/web/public/user_pb";
import Protobuf from "@repo/protobuf/utils";
import AsyncFn from "@repo/utils/AsyncFn";
import Case from "@repo/utils/Case";
import UDate from "@repo/utils/UDate";
import UAccessor from "@repo/utils-solid/UAccessor";

import analytics from "~/analytics/client";

const dismissables = ["viewCommunity", "communityShowScoreDisclaimer"] as const;
type Dismissable = (typeof dismissables)[number];

const initialState = {
  authenticated: null as boolean | null,
  dismissed: null as { [Key in Dismissable]?: boolean } | null,
  pii: null as User.Pii.Readable | null,
  id: null as string | null,
};

export const { useUser } = FeatureStore.init("User", initialState).setup(
  ([store, setStore], { attachAction, wrap }) => {
    const accessors = wrap
      .accessors({
        authenticated: () => store.authenticated,
        dismissed: attachAction(
          (key: Dismissable) => store.dismissed?.[key],
          () => actions.fetchDismissed(),
          () => () => store.dismissed
        ),
        pii: () => store.pii,
        id: () => store.id,
      })
      .finalize();

    const mutations = wrap
      .mutations({
        update(user: User) {
          analytics.identify(user.id, { email: user.pii.email });

          setStore("pii", user.pii);
          setStore("id", user.id);
        },
      })
      .finalize();

    const actions = wrap
      .actions({
        init() {
          const start = Date.now();
          return usRpc.Account.getInitPage({}).then(({ user, serverTime }) => {
            UDate.GlobalMod.shift(
              Protobuf.Timestamp.toNumber(serverTime!) -
                Date.now() +
                (Date.now() - start) / 2
            );

            if (!user) return setStore("authenticated", false);
            batch(() => {
              setStore("authenticated", true);
              mutations.update(UserUsa.normalize(user));
            });
          });
        },
        fetch: AsyncFn.deferrable(() =>
          usRpc.Account.getUserInfo({}).then(({ user }) => {
            return () => {
              mutations.update(UserUsa.normalize(user!));
            };
          })
        ),
        async dismiss(key: Dismissable) {
          if (
            await UAccessor.waitUntil(
              () => accessors.dismissed(key),
              (value) => value !== undefined
            )
          )
            return;

          setStore("dismissed", key, true);
          return usRpc.Account.dismiss({
            dismissable: {
              name: UsDismissablePb.Name[Case.fromCamel(key).toConstant()],
            },
          })
            .SUCCESS(() => {
              // Do nothing
            })
            .elseThrow(`Failed to dismiss ${key}`)
            .catch(() => {
              setStore("dismissed", key, false);
            });
        },
        fetchDismissed() {
          return usRpc.Account.getSiteVars({}).then(({ dismissed }) => {
            const dismissedByNormalizedName = Object.fromEntries(
              dismissed
                .filter(({ name }) => UsDismissablePb.Name[name])
                .map(({ name }) => [
                  Case.fromConstant(UsDismissablePb.Name[name]).toCamel(),
                  true,
                ])
            );

            setStore(
              "dismissed",
              Object.fromEntries(
                dismissables.map((name) => [
                  name,
                  dismissedByNormalizedName[name] || false,
                ])
              )
            );
          });
        },
      })
      .finalize();

    return { accessors, actions };
  }
);
